import { createInsertSchema } from "../../utils/sql";
import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import type { QueryRowType, Transaction, ZeRow } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { Nutrient } from "../nutrient";
import { Food } from "../food";
import { RecipeVariation } from "../recipe-variation";
import { FoodCategory } from "../food-category";
import {
  createId,
  accessDenied,
  sortIndexBetween,
  zFormFloat,
  nSortIndexesBetween,
  must,
} from "@corpuscomplete/core/utils";
import { type FormSchemaType, safeParseInt } from "@corpuscomplete/core/utils";
import { Context, zql } from "../../zero/schema";
import { RecipeBase } from "../recipe-base";
import { recipe, recipeIngredient, recipeStep } from "./recipe.sql";
import type { UserActor } from "@corpuscomplete/core/actor";

/**
 * Recipe namespace boundary for versioned recipe CRUD and nutrient derivation.
 *
 * Invariants:
 * - Recipe versions are immutable snapshots of ingredients/steps/servings.
 * - Derived recipe nutrients are recomputed from ingredient serving data.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace Recipe {
  export const byId = defineQuery(z.object({ id: z.string() }), ({ args, ctx }) =>
    RecipeBase.query()
      .whereExists("metaIfCurrent", (q) =>
        q.where((e) => Food.viewable(e, ctx.actor)).where("timeArchived", "IS", null),
      )
      .where("id", "=", args.id)
      .related("variations", (q) => RecipeVariation.patchQuery(q, "curated"))
      .one(),
  );
  type RawInfo = NonNullable<QueryRowType<typeof byId>>;

  /**
   * Historical/versioned recipe detail.
   *
   * Contract: this query only returns curated variations in `recipe.variations`.
   * Custom variations are intentionally omitted from this payload shape.
   */
  export const byIdVersion = defineQuery(
    z.object({ id: z.string(), version: z.number().positive() }),
    ({ ctx, args }) =>
      RecipeBase.query()
        .related("base", (q) =>
          q
            .related("meta")
            .related("servings", (q) => q.orderBy("sortIndex", "asc"))
            .related("categoryAssociations"),
        )
        .whereExists("meta", (q) => q.where((e) => Food.viewable(e, ctx.actor)))
        .where("id", "=", args.id)
        .where("version", "=", args.version)
        .related("variations", (q) => RecipeVariation.patchQuery(q.related("base"), "curated"))
        .one(),
  );

  export function asInfo(raw: RawInfo | undefined) {
    if (!raw) {
      return undefined;
    }
    const base = RecipeBase.asInfo(raw);
    if (!base) {
      return undefined;
    }
    const variations: RecipeVariation.Info[] = [];
    for (const variation of raw.variations) {
      const hydrated = RecipeVariation.asInfo(variation);
      if (hydrated) {
        variations.push(hydrated);
      }
    }
    return {
      ...base,
      variations,
    };
  }
  export type Info = NonNullable<ReturnType<typeof asInfo>>;

  export const listItemById = defineQuery(
    z.object({
      collectionId: z.string().optional(),
      id: z.uuid(),
    }),
    ({ ctx, args: { collectionId, id } }) => {
      let query = zql.recipe
        .where("id", "=", id)
        .whereExists("metaIfCurrent", (q) => {
          let baseQuery = q
            .where((e) => Food.viewable(e, ctx.actor))
            .where("timeArchived", "IS", null)
            .where("type", "=", "recipe");
          if (collectionId) {
            baseQuery = baseQuery.where("collectionId", "=", collectionId);
          }
          return baseQuery;
        })
        .related("base", (q) => q.related("meta"))
        .related("serving", (q) => q.related("base"))
        .one();
      return query;
    },
  );
  export type ListItem = NonNullable<QueryRowType<typeof listItemById>>;

  const listCursorSchema = z.object({
    id: z.string(),
  });
  export type ListCursor = z.infer<typeof listCursorSchema>;

  export const listItems = defineQuery(
    z.object({
      collectionId: z.string().optional(),
      limit: z.number(),
      cursor: listCursorSchema.nullable(),
      dir: z.union([z.literal("forward"), z.literal("backward")]),
    }),
    ({ ctx, args: { collectionId, limit, cursor, dir } }) => {
      const orderByDir = dir === "forward" ? "desc" : "asc";
      let q = zql.recipe
        .whereExists("metaIfCurrent", (q) => {
          let baseQuery = q
            .where((e) => Food.viewable(e, ctx.actor))
            .where("timeArchived", "IS", null)
            .where("type", "=", "recipe");
          if (collectionId) {
            baseQuery = baseQuery.where("collectionId", "=", collectionId);
          }
          return baseQuery;
        })
        .related("base", (q) => q.related("meta"))
        .related("serving", (q) => q.related("base"))
        .orderBy("id", orderByDir)
        .limit(limit);

      if (cursor) {
        q = q.start(cursor, { inclusive: false });
      }

      return q;
    },
  );

  async function assertValidIngredients(
    tx: Transaction,
    actor: UserActor,
    recipeId: string,
    ingredients: Array<{ foodServingId: string }>,
  ) {
    const ingredientServingIds = ingredients.map((ingredient) => ingredient.foodServingId);
    const servingData = await tx.run(
      zql.foodServing
        .where("id", "IN", ingredientServingIds)
        .related("food", (q) => q.related("meta")),
    );
    const ingredientFoodIds = servingData.map((serving) => serving.foodId);
    if (ingredientFoodIds.includes(recipeId)) {
      throw new Error("A recipe cannot contain itself as an ingredient");
    }
    const canViewIngredients = await Food.canViewBatch(tx, actor, ingredientFoodIds);
    if (!canViewIngredients) {
      accessDenied("You do not have permission to use one or more ingredients");
    }
  }

  async function createWithoutMetaOrVariations(
    tx: Transaction,
    { actor }: Context,
    {
      meta,
      serving: { perRecipe, ...servingInput },
      steps,
      ingredients,
      recipe,
      base,
      categoryAssociations,
      id,
    }: Omit<Create, "variations">,
    {
      version,
      timeCreated,
    }: {
      version: number;
      timeCreated: Temporal.Instant;
      timeUpdated: Temporal.Instant;
    },
  ): Promise<RecipeBase.Info> {
    const ingredientServingIds = ingredients.map((ingredient) => ingredient.foodServingId);
    const [servingData, units] = await Promise.all([
      tx.run(
        zql.foodServing
          .where("id", "IN", ingredientServingIds)
          .related("food", (q) => q.related("meta")),
      ),
      tx.run(zql.unit.where("type", "IN", ["mass", "volume"])),
    ]);
    const ingredientFoodIds = servingData.map((serving) => serving.foodId);
    if (ingredientFoodIds.includes(id)) {
      throw new Error("A recipe cannot contain itself as an ingredient");
    }
    const canViewIngredients = await Food.canViewBatch(tx, actor, ingredientFoodIds);
    if (!canViewIngredients) {
      accessDenied("You do not have permission to use one or more ingredients");
    }

    // TODO: Would be better to handle this the same way in this module and RecipeVariation module
    // But that would require refactoring RecipeVariation to hydrate after applying the patch
    const nutrientAmounts = RecipeBase.ingredientNutrients(ingredients, servingData);
    const nutrientRows = await Nutrient.amountsToRowsForKeys(tx, nutrientAmounts);

    await tx.mutate.food.insert({
      id,
      version,
      timeCreated,
      name: base.name,
      modifiers: base.modifiers,
      iconSlugs: base.iconSlugs ?? null,
    });

    // Insert new recipe record
    await tx.mutate.recipe.insert({
      id,
      version,
      description: recipe.description ?? null,
      prepTimeMinutes: recipe.prepTimeMinutes ?? null,
      cookTimeMinutes: recipe.cookTimeMinutes ?? null,
    });

    await Promise.all(
      steps
        .filter((step) => Boolean(step.text))
        .map(async (step) =>
          tx.mutate.recipeStep.insert({
            id: step.id,
            sortIndex: step.sortIndex,
            text: step.text,
            recipeId: id,
            recipeVersion: version,
          }),
        ),
    );

    await Promise.all(
      ingredients.map(async (ingredient) =>
        tx.mutate.recipeIngredient.insert({
          id: ingredient.id,
          sortIndex: ingredient.sortIndex,
          foodServingId: ingredient.foodServingId,
          amount: ingredient.amount,
          recipeId: id,
          recipeVersion: version,
        }),
      ),
    );

    await FoodCategory.addAssociations(tx, {
      foodId: id,
      foodVersion: version,
      isACategoryIds: categoryAssociations.map((association) => association.categoryId),
      ingredientFoods: servingData.map((serving) => ({
        foodId: serving.foodId,
        foodVersion: serving.foodVersion,
      })),
    });

    await Promise.all(
      nutrientRows.map(({ nutrientId, amount }) =>
        tx.mutate.recipeNutrient.insert({
          recipeId: id,
          recipeVersion: version,
          nutrientId,
          amount,
        }),
      ),
    );

    const serving: Food.ServingInput = {
      ...servingInput,
      nutrients: Nutrient.scaleAmounts(nutrientAmounts, 1 / perRecipe),
    };

    Food.assertNoDuplicateUnitServings([serving], units);

    const servings = Food.sortServings(Food.addUnitConversionServings([serving], units), units);
    await Promise.all(
      servings.map((unitServing) =>
        tx.mutate.foodServing.insert({
          ...Food.servingInputToRow(unitServing),
          foodId: id,
          foodVersion: version,
        }),
      ),
    );

    await tx.mutate.recipeServing.insert({
      id: serving.id,
      recipeId: id,
      recipeVersion: version,
      perRecipe,
    });

    const ingredientsForReturn: Array<RecipeBase.Info["ingredients"][number]> = [];
    for (const ingredient of ingredients) {
      const maybeServing = servingData.find((serving) => serving.id === ingredient.foodServingId);
      if (!maybeServing?.food?.meta) {
        // Shouldn't be able to get here Food.canViewBatch() returns above
        throw new Error("Invariant violation: food serving not found");
      }
      const {
        food: { meta, ...food },
        ...serving
      } = maybeServing;
      ingredientsForReturn.push({
        ...ingredient,
        recipeId: id,
        recipeVersion: version,
        food: {
          ...food,
          meta,
        },
        serving,
      });
    }

    // Could just re-query this with RecipeBase.baseQuery() but that seems wasteful
    // On the other hand, the timestamp stuff is a bit fragile here
    return {
      id,
      version,
      base: base,
      meta: { ...meta, isArchived: false },
      categoryAssociations: categoryAssociations,
      description: recipe.description ?? null,
      prepTimeMinutes: recipe.prepTimeMinutes,
      cookTimeMinutes: recipe.cookTimeMinutes,
      steps: steps.map((step) => ({
        id: step.id,
        text: step.text,
        sortIndex: step.sortIndex,
        recipeId: id,
        recipeVersion: version,
      })),
      ingredients: ingredientsForReturn,
      serving: {
        ...serving,
        nutrients: Nutrient.normalizeAmounts(serving.nutrients),
        perRecipe,
        massQuantity: serving.mass?.quantity ?? null,
        massUnitKey: serving.mass?.unitKey ?? null,
        volumeQuantity: serving.volume?.quantity ?? null,
        volumeUnitKey: serving.volume?.unitKey ?? null,
        foodId: id,
        foodVersion: version,
        // TODO: Does this actually get used directly
        sortIndex: sortIndexBetween(null, null),
      },
    };
  }

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    z.object({
      id: z.uuid(),
      base: Food.baseSchema(),
      meta: Food.metaSchema(),
      categoryAssociations: Food.categoryAssociationsSchema(),
      recipe: createInsertSchema(recipe)
        .omit({ id: true, version: true })
        .extend({
          prepTimeMinutes: z.number().nonnegative().max(RecipeBase.MAX_TIME_MINUTES).nullable(),
          cookTimeMinutes: z.number().nonnegative().max(RecipeBase.MAX_TIME_MINUTES).nullable(),
        }),
      serving: Food.servingSchema.extend({
        perRecipe: z.number().positive().max(RecipeBase.MAX_QUANTITY),
      }),
      steps: createInsertSchema(recipeStep)
        .omit({ recipeId: true, recipeVersion: true })
        .extend({
          text: z.string().trim().max(5000),
        })
        .array(),
      ingredients: createInsertSchema(recipeIngredient)
        .omit({ recipeId: true, recipeVersion: true })
        .array()
        .min(1),
      variations: RecipeVariation.curatedSchema.array().max(RecipeBase.MAX_CURATED_VARIANTS),
    }),
    async ({ tx, ctx, args }) => {
      const timeNow = Temporal.Now.instant();
      const version = 1;

      const canAdd = await Food.canAddToCollection(tx, ctx.actor, args.meta.collectionId, "recipe");
      if (!canAdd) {
        accessDenied("You do not have permission to add a recipe to this collection");
      }

      await assertValidIngredients(tx, ctx.actor, args.id, args.ingredients);

      await tx.mutate.foodMeta.insert({
        id: args.id,
        type: "recipe",
        collectionId: args.meta.collectionId,
        currentVersion: null,
        visibility: args.meta.visibility,
        discoverability: args.meta.discoverability,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
      const recipeBase = await createWithoutMetaOrVariations(tx, ctx, args, {
        version,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });

      await tx.mutate.foodMeta.update({
        id: args.id,
        currentVersion: version,
      });

      if (args.variations.length > 0) {
        await Promise.all(
          args.variations.map((variation) =>
            RecipeVariation.create(
              tx,
              ctx.actor,
              {
                ...variation,
                recipeId: args.id,
                recipeVersion: version,
                meta: args.meta,
                categoryAssociations: variation.categoryAssociations,
              },
              recipeBase,
              {
                metaOp: "insert",
                kind: "curated",
                version: 1,
                timeCreated: timeNow,
                timeUpdated: timeNow,
              },
            ),
          ),
        );
      }
    },
  );

  export const formSchema = (type: FormSchemaType) => {
    return RecipeBase.formSchema(type).extend({
      meta: create.schema.shape.meta,
      serving: create.schema.shape.serving.omit({ id: true }).extend({
        perRecipe: zFormFloat(type),
        defaultAmount: zFormFloat(type),
      }),
      variations: RecipeBase.formSchema(type)
        .extend({ originalId: z.uuid().nullable() })
        .array()
        .max(RecipeBase.MAX_CURATED_VARIANTS),
    });
  };
  export type Form = z.input<ReturnType<typeof formSchema>>;
  export const formDefault = (collectionId: string | undefined): Form => ({
    ...RecipeBase.formDefault(),
    serving: {
      name: "serving(s)",
      perRecipe: "4",
      modifiers: [],
      defaultAmount: "1",
      mass: null,
      volume: null,
    },
    meta: Food.formMetaDefault(collectionId),
    variations: [],
  });

  export const formToCreate = (form: Form, id: string): Create => {
    const normalizedForm = formSchema("storage").parse(form);
    const { base, recipe, ingredients, steps, meta, serving, variations, categoryAssociations } =
      normalizedForm;
    const stepSortIndexes = nSortIndexesBetween(null, null, steps.length);
    const ingredientSortIndexes = nSortIndexesBetween(null, null, ingredients.length);
    return {
      id,
      base,
      meta,
      categoryAssociations,
      recipe: {
        description: recipe.description.trim() || null,
        prepTimeMinutes: safeParseInt(recipe.prepTimeMinutes),
        cookTimeMinutes: safeParseInt(recipe.cookTimeMinutes),
      },
      serving: {
        id: createId(),
        ...serving,
        defaultAmount: parseFloat(serving.defaultAmount),
        perRecipe: parseFloat(serving.perRecipe),
      },
      steps: steps.map((step, index) => ({
        id: createId(),
        sortIndex: must(stepSortIndexes[index]),
        ...step,
      })),
      ingredients: ingredients.map((ingredient, index) => ({
        id: createId(),
        sortIndex: must(ingredientSortIndexes[index]),
        ...ingredient,
      })),
      variations: variations.map((variation) => ({
        id: createId(),
        servingId: createId(),
        base: variation.base,
        categoryAssociations: variation.categoryAssociations,
        ...RecipeVariation.derivePatch(form, variation),
      })),
    };
  };

  export const infoToForm = (info: Info): Form => {
    return {
      base: info.base,
      meta: info.meta,
      categoryAssociations: Food.toFormCategoryAssociations(info.categoryAssociations),
      recipe: {
        description: info.description ?? "",
        prepTimeMinutes: info.prepTimeMinutes?.toString() ?? "",
        cookTimeMinutes: info.cookTimeMinutes?.toString() ?? "",
      },
      steps:
        info.steps.length > 0 ? info.steps.map((step) => ({ text: step.text })) : [{ text: "" }],
      ingredients: info.ingredients.map((ingredient) => {
        return {
          amount: ingredient.amount,
          foodServingId: ingredient.serving.id,
        };
      }),
      serving: {
        name: info.serving.name,
        perRecipe: info.serving.perRecipe.toString(),
        defaultAmount: info.serving.defaultAmount.toString(),
        modifiers: info.serving.modifiers,
        mass:
          info.serving.massQuantity && info.serving.massUnitKey
            ? {
                quantity: info.serving.massQuantity,
                unitKey: info.serving.massUnitKey,
              }
            : null,
        volume:
          info.serving.volumeQuantity && info.serving.volumeUnitKey
            ? {
                quantity: info.serving.volumeQuantity,
                unitKey: info.serving.volumeUnitKey,
              }
            : null,
      },
      variations: info.variations.flatMap((variation) => {
        const { value: variationInfo } = RecipeVariation.applyPatch(info, variation, {
          ...variation,
          meta: { ...variation.meta, isArchived: false },
        });
        return {
          originalId: variation.id,
          base: {
            name: variationInfo.base.name,
            modifiers: variationInfo.base.modifiers,
            iconSlugs: variationInfo.base.iconSlugs,
          },
          categoryAssociations: Food.toFormCategoryAssociations(variationInfo.categoryAssociations),
          recipe: {
            description: variationInfo.description ?? "",
            prepTimeMinutes: variationInfo.prepTimeMinutes?.toString() ?? "",
            cookTimeMinutes: variationInfo.cookTimeMinutes?.toString() ?? "",
          },
          ingredients: variationInfo.ingredients.map((ingredient) => ({
            amount: ingredient.amount,
            sortIndex: ingredient.sortIndex,
            foodServingId: ingredient.serving.id,
          })),
          steps: variationInfo.steps.map((step) => ({
            text: step.text,
            sortIndex: step.sortIndex,
          })),
        };
      }),
    };
  };

  export const formToEdit = (
    form: Form,
    { id: recipeId, version: recipeVersion, variations: currentVariations }: Info,
  ): Edit => {
    const normalizedForm = formSchema("storage").parse(form);
    const {
      base,
      recipe,
      ingredients,
      steps,
      meta,
      serving,
      variations: newVariations,
      categoryAssociations,
    } = normalizedForm;
    const currentVariationVersions = new Map(
      currentVariations.map((variation) => [variation.id, variation.version]),
    );
    const submittedVariationIds = new Set(
      newVariations.flatMap((variation) => (variation.originalId ? [variation.originalId] : [])),
    );

    const addVariations: Edit["addVariations"] = [];
    const editVariations: Edit["editVariations"] = [];
    const removeVariations: Edit["removeVariations"] = [];

    for (const variation of newVariations) {
      const variationCurrentVersion = variation.originalId
        ? currentVariationVersions.get(variation.originalId)
        : undefined;

      if (variationCurrentVersion !== undefined) {
        editVariations.push({
          id: variation.originalId ?? createId(),
          servingId: createId(),
          currentVersion: variationCurrentVersion,
          base: variation.base,
          categoryAssociations: variation.categoryAssociations,
          ...RecipeVariation.derivePatch(form, variation),
        });
      } else {
        addVariations.push({
          id: variation.originalId ?? createId(),
          servingId: createId(),
          base: variation.base,
          categoryAssociations: variation.categoryAssociations,
          ...RecipeVariation.derivePatch(form, variation),
        });
      }
    }

    for (const variation of currentVariations) {
      if (!submittedVariationIds.has(variation.id)) {
        removeVariations.push({
          id: variation.id,
          version: variation.version,
        });
      }
    }

    const stepSortIndexes = nSortIndexesBetween(null, null, steps.length);
    const ingredientSortIndexes = nSortIndexesBetween(null, null, ingredients.length);
    return {
      id: recipeId,
      currentVersion: recipeVersion,
      base,
      meta,
      categoryAssociations,
      recipe: {
        description: recipe.description.trim() || null,
        prepTimeMinutes: safeParseInt(recipe.prepTimeMinutes),
        cookTimeMinutes: safeParseInt(recipe.cookTimeMinutes),
      },
      serving: {
        id: createId(),
        ...serving,
        perRecipe: parseFloat(serving.perRecipe),
        defaultAmount: parseFloat(serving.defaultAmount),
      },
      steps: steps.map((step, index) => ({
        id: createId(),
        sortIndex: must(stepSortIndexes[index]),
        ...step,
      })),
      ingredients: ingredients.map((ingredient, index) => ({
        id: createId(),
        sortIndex: must(ingredientSortIndexes[index]),
        ...ingredient,
      })),
      addVariations,
      editVariations,
      removeVariations,
    };
  };

  export type Archive = z.infer<typeof archive.schema>;
  export const archive = withZeroTransaction(
    z.object({ id: z.uuid(), currentVersion: z.number() }),
    async ({ tx, ctx, args: { id, currentVersion } }) => {
      return Food.archive(tx, ctx.actor, { id, currentVersion, type: "recipe" });
    },
  );

  export type Edit = z.infer<typeof edit.schema>;
  export const edit = withZeroTransaction(
    create.schema
      .omit({
        variations: true,
      })
      .extend({
        currentVersion: z.number(),
        addVariations: RecipeVariation.curatedSchema.array(),
        editVariations: RecipeVariation.curatedSchema
          .extend({ currentVersion: z.number() })
          .array(),
        removeVariations: z.array(z.object({ id: z.uuid(), version: z.number() })),
      }),
    async ({ tx, ctx, args }) => {
      const canEditResult = await Food.canEdit(
        tx,
        ctx.actor,
        {
          id: args.id,
          currentVersion: args.currentVersion,
          type: "recipe",
        },
        { op: "edit", collectionId: args.meta.collectionId },
      );
      if (!canEditResult.ok) {
        accessDenied(canEditResult.error);
      }
      const { currentMeta, newVersion } = canEditResult;

      const currentCuratedVariations = await tx.run(
        zql.recipeVariation
          .where("recipeId", "=", args.id)
          .where("recipeVersion", "=", args.currentVersion)
          .where("kind", "=", "curated"),
      );
      const curatedsValid = allCuratedsAccountedFor(args, currentCuratedVariations);
      if (!curatedsValid) {
        throw new Error(
          "Recipe.edit must include update/archive op for all currently-active curated variations",
        );
      }
      const activeCuratedCount =
        args.addVariations.length + args.editVariations.length - args.removeVariations.length;
      if (activeCuratedCount > RecipeBase.MAX_CURATED_VARIANTS) {
        throw new Error(
          `A recipe can have at most ${RecipeBase.MAX_CURATED_VARIANTS} curated variations`,
        );
      }

      const timeNow = Temporal.Now.instant();

      const recipeBase = await createWithoutMetaOrVariations(tx, ctx, args, {
        version: newVersion,
        timeCreated: currentMeta.timeCreated,
        timeUpdated: timeNow,
      });

      await tx.mutate.foodMeta.update({
        id: args.id,
        ...args.meta,
        currentVersion: newVersion,
        timeUpdated: timeNow,
      });

      const promises: Promise<any>[] = [];
      promises.push(
        Promise.all(
          args.addVariations.map((variation) =>
            RecipeVariation.create(
              tx,
              ctx.actor,
              {
                ...variation,
                recipeId: args.id,
                recipeVersion: newVersion,
                meta: args.meta,
              },
              recipeBase,
              {
                metaOp: "insert",
                kind: "curated",
                version: 1,
                timeCreated: timeNow,
                timeUpdated: timeNow,
              },
            ),
          ),
        ),
      );

      promises.push(
        Promise.all(
          args.editVariations.map((variation) =>
            RecipeVariation.create(
              tx,
              ctx.actor,
              {
                ...variation,
                meta: args.meta,
                recipeId: args.id,
                recipeVersion: newVersion,
              },
              recipeBase,
              {
                metaOp: "update",
                kind: "curated",
                version: newVersion,
                timeCreated: currentMeta.timeCreated,
                timeUpdated: timeNow,
              },
            ),
          ),
        ),
      );

      promises.push(
        Promise.all(
          args.removeVariations.map(({ id }) =>
            tx.mutate.foodMeta.update({
              id,
              timeArchived: timeNow,
              timeUpdated: timeNow,
            }),
          ),
        ),
      );

      await Promise.all(promises);
    },
  );

  function allCuratedsAccountedFor(
    {
      editVariations,
      removeVariations,
    }: Pick<Edit, "addVariations" | "editVariations" | "removeVariations">,
    currentCurateds: ZeRow<typeof zql.recipeVariation>[],
  ): boolean {
    const currentCuratedsById = new Map(currentCurateds.map((curated) => [curated.id, curated]));

    const accountedForIds = new Set<string>();

    for (const curated of editVariations) {
      const currentCurated = currentCuratedsById.get(curated.id);
      if (!currentCurated || currentCurated.version !== curated.currentVersion) {
        return false;
      }

      accountedForIds.add(curated.id);
    }

    for (const { id, version } of removeVariations) {
      const currentCurated = currentCuratedsById.get(id);
      if (!currentCurated || currentCurated.version !== version) {
        return false;
      }

      accountedForIds.add(id);
    }

    for (const curated of currentCurateds) {
      if (!accountedForIds.has(curated.id)) {
        return false;
      }
    }

    return true;
  }
}
